// The ghost record format: pure data helpers shared by the game (src/sim/ghost.ts) and the
// leaderboard server (server/lib.ts). Keep this module free of Rapier, Renderoni and Three.js so the
// server bundle stays small: it may only import from src/config and other pure modules.
import { environmentById } from '../config/environments';
import { SIM, UPGRADES } from '../config/tuning';
import { vehicleById, type VehicleId } from '../config/vehicles';
import { UPGRADE_IDS, type UpgradeLevels } from './upgrades';

export interface GhostRecord {
  version: number;
  envId: string;
  vehicle: VehicleId;
  levels: UpgradeLevels;
  /** Start-line ticks before the first throttle. */
  idle: number;
  /** Run-length-encoded throttle in hundredths: "value*count,value*count,…". */
  inputs: string;
  /** Distance the recorded run reached; the replay must reach exactly the same. */
  distance: number;
}

/** Longest run a ghost may hold: two hours of running ticks. */
export const MAX_GHOST_TICKS = 2 * 60 * 60 * SIM.tickRateHz;

/** Run-length encoding of the per-tick throttle (keyboard and pedal input is mostly long holds). */
export function encodeInputs(inputs: readonly number[]): string {
  const out: string[] = [];
  for (let i = 0; i < inputs.length; ) {
    let j = i;
    while (j < inputs.length && inputs[j] === inputs[i]) j++;
    out.push(`${inputs[i]}*${j - i}`);
    i = j;
  }
  return out.join(',');
}

/** Parses the run-length pairs, throwing on anything malformed. */
function parsePairs(text: string): Array<[number, number]> {
  const pairs: Array<[number, number]> = [];
  if (!text) return pairs;
  for (const part of text.split(',')) {
    const bits = part.split('*');
    if (bits.length !== 2) throw new Error('bad ghost input');
    const [v, n] = bits.map(Number);
    if (!Number.isInteger(v) || !Number.isInteger(n) || n < 1 || v < -100 || v > 100) throw new Error('bad ghost input');
    pairs.push([v, n]);
  }
  return pairs;
}

/** Number of ticks the encoded inputs hold, without expanding them (safe on untrusted text). */
export function countInputs(text: string): number {
  let total = 0;
  for (const [, n] of parsePairs(text)) total += n;
  return total;
}

export function decodeInputs(text: string): number[] {
  const out: number[] = [];
  for (const [v, n] of parsePairs(text)) for (let k = 0; k < n; k++) out.push(v);
  return out;
}

/** Validates untrusted saved data; null if it can't replay under this build. */
export function sanitizeGhost(raw: unknown): GhostRecord | null {
  const g = raw as Partial<GhostRecord> | null;
  if (!g || typeof g !== 'object') return null;
  if (g.version !== SIM.version || typeof g.envId !== 'string' || !environmentById(g.envId)) return null;
  if (typeof g.vehicle !== 'string' || !vehicleById(g.vehicle)) return null;
  if (typeof g.idle !== 'number' || !Number.isInteger(g.idle) || g.idle < 0 || typeof g.inputs !== 'string') return null;
  if (typeof g.distance !== 'number' || !Number.isFinite(g.distance)) return null;
  const levels = {} as UpgradeLevels;
  for (const id of UPGRADE_IDS) {
    const v = (g.levels as Record<string, unknown> | undefined)?.[id];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > UPGRADES.maxLevel) return null;
    levels[id] = v;
  }
  try {
    // counted, not expanded: a tiny string like "0*999999999" must not allocate a huge array
    if (countInputs(g.inputs) > MAX_GHOST_TICKS) return null;
  } catch {
    return null;
  }
  return { version: g.version, envId: g.envId, vehicle: g.vehicle as VehicleId, levels, idle: g.idle, inputs: g.inputs, distance: g.distance };
}
